import { useMemo, useRef, useState, UIEvent, useCallback, useLayoutEffect } from 'react'

import { Row, Table as ReactTableType } from '@tanstack/react-table'
import { debounce } from 'lodash-es'
import { useShallow } from 'zustand/react/shallow'

import {
  BodyRow,
  getColumnWidth,
  HeadRow,
  InstanceTransferProps,
  LoadingBodyRow,
  ActionMenu as ActionMenuWrapper,
  Wrapper,
  Root,
  Head,
  Body,
  ScrollToTopButton,
} from '@shared/components/Table'
import {
  useScrollRestorationStore,
  selectScrollRestorationSetters,
  useSelectLastKnownScrollPosition,
} from 'stores/scrollRestoration'

type FixedInstanceProps<D> = {
  tableInstance: ReactTableType<D>
  enableSelection: boolean
} & InstanceTransferProps<D>

const FixedInstanceRaw = <D,>({
  tableInstance,
  enableSelection,
  enableLinking,
  LinkProps,
  excludeFieldsFromLinking,
  enableClickAction,
  clickAction,
  isInitialLoadingGlobal,
  isInitialLoadingByRowByID,
  RowActions,
  ActionMenuProps,
  renderSubComponent,
  hideHeader,
  onScroll,
  ScrollTopButton,
}: FixedInstanceProps<D>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const selectedRow = useRef<Row<D>>(undefined)
  const parentRef = useRef<HTMLDivElement>(null)

  const { setLastKnownScrollPosition } = useScrollRestorationStore(useShallow(selectScrollRestorationSetters))
  const lastKnownScrollPosition = useRef(useSelectLastKnownScrollPosition(window.location.pathname))

  const { rows } = tableInstance.getRowModel()

  const columnWidth = useMemo(
    () => getColumnWidth({ tableInstance, enableSelection, enableActionColumn: !!RowActions }),
    [RowActions, enableSelection, tableInstance],
  )

  const debouncedHandleScroll = useMemo(
    () =>
      debounce((value: number) => {
        setLastKnownScrollPosition(window.location.pathname, value)
      }, 500),
    [setLastKnownScrollPosition],
  )

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      onScroll?.(parentRef.current?.scrollTop || 0)

      return debouncedHandleScroll(e.currentTarget.scrollTop)
    },
    [debouncedHandleScroll, onScroll],
  )

  const handleActionMenuClose = useCallback(() => {
    setAnchorEl(undefined)

    setTimeout(() => {
      selectedRow.current = undefined
    }, 300)
  }, [])

  useLayoutEffect(() => {
    if (lastKnownScrollPosition.current) {
      parentRef.current?.scrollTo({ top: lastKnownScrollPosition.current })
    }
  }, [])

  return (
    <Wrapper ref={parentRef} onScroll={handleScroll}>
      {parentRef.current?.scrollTop && parentRef.current.scrollTop > 0 ? (
        ScrollTopButton ? (
          ScrollTopButton(parentRef)
        ) : (
          <ScrollToTopButton
            onClick={() => {
              parentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        )
      ) : null}
      <Root>
        {!hideHeader && (
          <Head>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <HeadRow
                key={headerGroup.id}
                tableInstance={tableInstance}
                row={headerGroup}
                columnWidth={columnWidth}
                enableSelection={enableSelection}
                enableActionColumn={!!RowActions}
                isInitialLoading={isInitialLoadingGlobal}
              />
            ))}
          </Head>
        )}
        {isInitialLoadingGlobal ? (
          <Body>
            {[...Array(10)].map((_, idx) => (
              <LoadingBodyRow
                key={idx}
                tableInstance={tableInstance}
                columnWidth={columnWidth}
                enableSelection={enableSelection}
                enableActionColumn={!!RowActions}
              />
            ))}
          </Body>
        ) : (
          <Body>
            {rows.map((row) => (
              <BodyRow
                key={row.id}
                tableInstance={tableInstance}
                row={row}
                enableSelection={enableSelection}
                enableLinking={enableLinking}
                LinkProps={LinkProps}
                excludeFieldsFromLinking={excludeFieldsFromLinking}
                enableClickAction={enableClickAction}
                clickAction={clickAction}
                columnWidth={columnWidth}
                isInitialLoading={isInitialLoadingGlobal}
                isInitialLoadingByRowByID={isInitialLoadingByRowByID}
                AnimatedButtonProps={
                  RowActions
                    ? {
                        open: !!anchorEl && selectedRow.current?.index === row.index,
                        IconButtonProps: {
                          onClick: (e) => {
                            selectedRow.current = row
                            const target = e.currentTarget

                            setTimeout(() => {
                              setAnchorEl(target)
                            }, 0)
                          },
                        },
                        IconProps: {
                          fontSize: 'medium',
                        },
                      }
                    : undefined
                }
                renderSubComponent={renderSubComponent}
              />
            ))}
          </Body>
        )}
      </Root>

      {!!RowActions && (
        <ActionMenuWrapper
          {...(selectedRow.current && ActionMenuProps(selectedRow.current))}
          anchorEl={anchorEl}
          onClose={handleActionMenuClose}
        >
          {!!selectedRow.current && RowActions({ row: selectedRow.current, onClose: handleActionMenuClose })}
        </ActionMenuWrapper>
      )}
    </Wrapper>
  )
}

export const FixedInstance = FixedInstanceRaw
